import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import type { Evaluation } from '@/types/evaluation.types';
import { EvaluationStatus } from '@/types/evaluation.types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { StatusBadge } from '@/components/evaluation-status/StatusBadge';
import { Calendar, FileText, Users } from 'lucide-react';

interface EvaluationCardProps {
  evaluation: Evaluation;
}

export function EvaluationCard({ evaluation }: EvaluationCardProps) {
  const { t } = useTranslation('home');
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/evaluation/${evaluation.id}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const formatTargetDate = (date: Date | null): string => {
    if (!date) return t('common:date.notSet');
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  // Counts
  const solutionCount = evaluation.targetSolutions.length;
  const participantCount = evaluation.participants.length;

  // Get actual solution names (first 3)
  const solutionNames = evaluation.targetSolutions.slice(0, 3).map(s => s.solutionName);
  const hasMoreSolutions = evaluation.targetSolutions.length > 3;

  // Get participant names (first 3)
  const participantNames = evaluation.participants.slice(0, 3).map(p => p.name);
  const hasMoreParticipants = evaluation.participants.length > 3;

  // Calculate completion percentage for Active evaluations
  // For simplicity, we'll calculate based on how many criteria groups have been defined
  // In a real app, this would be based on scorecard submission status
  const calculateProgress = (): number | null => {
    if (evaluation.status !== EvaluationStatus.Active) return null;

    // Simple heuristic: if there are solutions, participants, and criteria, show 0% as baseline
    // This is a placeholder - real implementation would check scorecard submission
    const hasRequiredData =
      evaluation.targetSolutions.length > 0 &&
      evaluation.participants.length > 0 &&
      evaluation.criteriaGroups.length > 0;

    return hasRequiredData ? 0 : null;
  };

  const progressPercent = calculateProgress();

  return (
    <Card
      data-testid={`evaluation-card-${evaluation.id}`}
      role="button"
      tabIndex={0}
      className="cursor-pointer transition-all hover:shadow-md hover:border-primary/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={`View evaluation: ${evaluation.name}`}
      style={{ padding: 'var(--card-padding)' }}
    >
      <CardHeader>
        <div className="flex items-start justify-between gap-3 mb-2">
          <CardTitle className="text-2xl font-bold flex-1">{evaluation.name}</CardTitle>
          <StatusBadge status={evaluation.status} size="sm" />
        </div>
      </CardHeader>
      <CardContent style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}>
        {/* Target Date */}
        {evaluation.targetCompletionDate && (
          <div className="flex items-center text-sm text-muted-foreground" style={{ gap: 'var(--gap)' }}>
            <Calendar className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span>{formatTargetDate(evaluation.targetCompletionDate)}</span>
          </div>
        )}

        {/* Solutions */}
        {solutionCount > 0 && (
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
              <FileText className="h-3.5 w-3.5" aria-hidden="true" />
              <span>{t('card.solutionsLabel', { count: solutionCount })}</span>
            </div>
            <div className="text-sm pl-5">
              {solutionNames.join(', ')}
              {hasMoreSolutions && <span className="text-muted-foreground"> +{solutionCount - 3} more</span>}
            </div>
          </div>
        )}

        {/* Participants */}
        {participantCount > 0 && (
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
              <Users className="h-3.5 w-3.5" aria-hidden="true" />
              <span>{t('card.participantsLabel', { count: participantCount })}</span>
            </div>
            <div className="text-sm pl-5">
              {participantNames.join(', ')}
              {hasMoreParticipants && <span className="text-muted-foreground"> +{participantCount - 3} more</span>}
            </div>
          </div>
        )}

        {/* Progress Bar for Active Evaluations */}
        {progressPercent !== null && (
          <div className="space-y-2 pt-2 border-t">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{t('card.progress', { percent: progressPercent })}</span>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </div>
        )}

        {/* Last Updated */}
        <div className="text-xs text-muted-foreground border-t pt-2">
          {t('card.updated')} {formatDistanceToNow(evaluation.modifiedDate, { addSuffix: true })}
        </div>
      </CardContent>
    </Card>
  );
}
